import React, { useEffect, useState, useMemo, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Save, Plus, X, Type, Info, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { ETabs } from "../../../components/ETabs"; 
import { Brand } from "../../../types/brand";
import { toast } from "react-toastify";
import { uploadBrandLogoToServer, deleteBrandLogoFromServer } from "../../../service/brandImageService";

interface BrandDialogProps {
  isOpen: boolean;
  onClose: () => void;
  brand: Brand | null;
  onSave: (data: any) => void; 
}

export const BrandDialog: React.FC<BrandDialogProps> = ({ isOpen, onClose, brand, onSave }) => {
  const [formData, setFormData] = useState({ name: "", description: "", source: "APP", logoUrl: "" });
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (brand) {
      setFormData({
        name: brand.name || "",
        description: brand.description || "", 
        source: brand.source || "APP",
        logoUrl: brand.logoUrl || "",
      });
    } else {
      setFormData({ name: "", description: "", logoUrl: "", source: "APP" });
    }
    setActiveIndex(0); 
  }, [isOpen, brand]);

  // --- FORM SUBMIT (Ana Kayıt) ---
const handleSubmit = () => {
  if (!formData.name.trim()) {
    toast.error("Marka adı boş bırakılamaz.");
    setActiveIndex(0);
    return;
  }

  // source alanını çıkartarak sadece gerekli verileri gönderiyoruz
  const { source, ...payload } = formData; 
  
  onSave(payload); // Backend'in beklediği temiz veri
};

  // --- RESİM YÜKLEME (Ayrı Servis) ---
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!brand?.id) {
      toast.warning("Lütfen önce markayı kaydedin, sonra logo yükleyin.");
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadBrandLogoToServer(file, brand.id);
      setFormData((p) => ({ ...p, logoUrl: result.logoUrl }));
      toast.success("Logo yüklendi.");
    } catch (error: any) {
      toast.error("Yükleme başarısız.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async () => {
    if (!brand?.id) return;
    const success = await deleteBrandLogoFromServer(brand.id);
    if (success) {
      setFormData((p) => ({ ...p, logoUrl: "" }));
      toast.success("Logo silindi.");
    }
  };

  const tabs = useMemo(() => [
    {
      id: 'basic',
      header: <><Type className="w-4 h-4 mr-2" /> Genel Bilgiler</>,
      content: (
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-sm">Marka Adı *</label>
            <InputText value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-sm">Açıklama</label>
            <InputTextarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} rows={3} />
          </div>
        </div>
      )
    },
    {
      id: 'image',
      header: <><ImageIcon className="w-4 h-4 mr-2" /> Marka Logosu</>,
      content: (
        <div className="p-4 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-sm text-gray-700">Logo Yönetimi</h3>
            <label className={`cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all hover:bg-indigo-700 ${isUploading || !brand?.id ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <Upload size={16} /> {isUploading ? 'Yükleniyor...' : 'Fotoğraf Seç'}
              <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileSelect} disabled={isUploading || !brand?.id} accept="image/*" />
            </label>
          </div>
          <div className="flex justify-center items-center border-2 border-dashed rounded-xl p-8 bg-gray-50/50 min-h-[200px]">
            {formData.logoUrl ? (
              <div className="relative group shadow-md p-2 bg-white rounded-lg">
                <img src={formData.logoUrl} className="max-h-48 w-auto object-contain rounded" alt="Önizleme" />
                <button onClick={handleRemoveImage} className="absolute -top-3 -right-3 bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <ImageIcon size={64} className="mx-auto mb-2 opacity-10" />
                <p className="text-sm italic">Henüz bir logo yüklenmedi.</p>
                {!brand?.id && <p className="text-[10px] text-amber-600 mt-2 font-bold uppercase">⚠️ Önce markayı oluşturun</p>}
              </div>
            )}
          </div>
        </div>
      )
    }
  ], [formData, isUploading, brand?.id]);

  return (
    <Dialog
      header={brand ? `Markayı Düzenle: ${brand.name}` : "Yeni Marka Tanımla"}
      visible={isOpen} onHide={onClose} modal className="w-full lg:w-4/12"
      footer={
        <div className="flex justify-end gap-2">
          <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
          <IconTextButton onClick={handleSubmit} icon={brand ? Save : Plus} className="btn-new">
            {brand ? "Güncelle" : "Markayı Kaydet"}
          </IconTextButton>
        </div>
      }
    >
      <ETabs tabs={tabs} initialActiveIndex={activeIndex} onTabChange={setActiveIndex} />
    </Dialog>
  );
};